import { ProposalDto } from '@gap/backend/app-services/proposal/models/ProposalDto';
import { notNull } from '@gap/utils/utils';
import { apiClient } from 'api/trpc/trpc-client';
import { formHandler } from 'reusable/mobx-store-utils/form-handlers/new/form-handler';
import { createStoreContext } from 'reusable/mobx-store-utils/storeUtils';
import ProposalsViewProposalStore from '../../ProposalsViewProposalStore';
import { PaymentMethodOptionValue, ServiceRateTypeEnum } from '@gap/backend/app-services/services/models/ServiceRateTypesDto';
import DateUtils from '@gap/utils/date-utils';
import OrgGuardStore from 'features/main/main-router/org-guard/OrgGuardStore';
import { Analytics, AnalyticsEvent } from 'analytics/analytics';
import userCache from 'cache/userCache';

interface ProposalSectionAcceptanceStoreProps {
   proposal: ProposalDto,
   proposalsViewProposalStore: ProposalsViewProposalStore,
   orgGuardStore: OrgGuardStore
}

export default class ProposalSectionAcceptanceStore {
   sp
   toeUrl: string | undefined
   acceptanceFields = {
      agreeToProposalTerms: false,
      agreeToTermsOfEngagement: false,
   }

   constructor(sp: ProposalSectionAcceptanceStoreProps) {
      this.sp = sp
      if (!!sp.proposal.acceptedAt) {
         this.acceptanceFields.agreeToProposalTerms = true
         this.acceptanceFields.agreeToTermsOfEngagement = true
      }
   }

   get signees() {
      return this.sp.proposalsViewProposalStore.signees
   }

   get acceptanceForm() {
      return formHandler(this.acceptanceFields, {
         agreeToProposalTerms: { type: 'boolean' },
         agreeToTermsOfEngagement: { type: 'boolean' },
      })
   }

   get currentUserAcceptsApplicableTerms() {
      return this.acceptanceFields.agreeToProposalTerms
         && (this.step4?.termsOfEngagementFilename
            ? this.acceptanceFields.agreeToTermsOfEngagement
            : true)
   }

   get step4() {
      return notNull(this.sp.proposal.step4)
   }

   get dueDate() {
      return this.step4?.dueDateDate && DateUtils.formatDate(new Date(notNull(this.step4.dueDateDate)), 'date-full-day')
   }

   get acceptanceWindow() {
      if (!this.sp.proposal.step4 || (!this.sp.proposal.step4.dueDate && !this.sp.proposal.step4.dueDateDate)) {
         return undefined
      }

      switch (this.sp.proposal.step4.dueDate) {
         case '1-week-from-now':
            return '5 working day'
         case '2-weeks-from-now':
            return '10 working day'
         default:
            return undefined
      }
   }

   get isReadyToSign() {
      return this.currentUserAcceptsApplicableTerms
         && this.sp.proposalsViewProposalStore.arePaymentPreferencesComplete
   }

   get isServiceUser() {
      return !!userCache.serviceUser.get()
   }

   fetchTermsOfEngagement = async () => {
      if (this.step4.termsOfEngagementFilename && !this.isServiceUser) {
         const result = await apiClient.proposals.getProposalsTOEViewUrl.query({
            fileName: this.step4.termsOfEngagementFilename,
            orgId: this.sp.proposal.advisorOrgId,
            proposalId: this.sp.proposal.id,
         })
         this.toeUrl = result.url
      }
   }

   signAndAccept = async () => {
      const paymentPreferences = this.sp.proposalsViewProposalStore.paymentPreferences as Partial<Record<ServiceRateTypeEnum, PaymentMethodOptionValue>>

      // Filter out payment preferences with null values
      const filteredPaymentPreferences = Object.fromEntries(
         Object.entries(paymentPreferences).filter(([_, value]) => value !== null)
      ) as Partial<Record<ServiceRateTypeEnum, PaymentMethodOptionValue>>


      const { response, acceptedAt } = await apiClient.proposals.addProposalResponse.mutate({
         proposalId: this.sp.proposal.id,
         agree: true,
         advisorOrgId: notNull(this.sp.orgGuardStore.org?.advisorOrg?.id),
         ...(this.sp.proposal.response ? {} : {
            includedOptionalServiceIds: this.sp.proposalsViewProposalStore.includedOptionalServiceIds,
            paymentPreferences: filteredPaymentPreferences,
         }),
      })

      this.sp.proposalsViewProposalStore.sp.proposalsViewStore.showSignedConfirmation = true

      this.sp.proposal.response = response
      this.sp.proposal.acceptedAt = acceptedAt

      // If the proposal acceptance criteria is met
      if (acceptedAt) {
         Analytics.emitEvent(AnalyticsEvent.proposalAccepted, undefined)
      }
   }
}

export const { Provider: ProposalSectionAcceptanceStoreProvider, useProvidedStore: useProposalSectionAcceptanceStore } =
   createStoreContext(ProposalSectionAcceptanceStore)
